import {useState, useRef, useReducer, useCallback, useEffect, useMemo} from 'react';
//Styles
import * as S from './LocationMap.styles';
import {
  LargeSectionHeader,
  CenteredSpaceWrap,
} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
//Icons
import MapLocationIcon from '../../../../../stories/Icons/MapLocationIcon';
import CloseCircleFillIcon from '../../../../../stories/Icons/CloseCircleFillIcon';
import CarIcon from '../../../../../stories/Icons/CarIcon';
import SomethingWentWrongIcon from '@stencil/icons/src/SomethingWentWrongIcon/SomethingWentWrongIcon';
//Config
import {gaConfig} from '@stencil/hotels-config';
import {detailsPagePushGaThunk} from '@stencil/hotels-ga/src';
import {detailsNavigationIds} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import {useDispatch, useSelector} from 'react-redux';
// ACTIONS
import {
  resetSelectedPlaces,
  showAllNearbyPlaces,
  showOnlyUserSelectedLocations,
  updateSelectedPlaces,
} from '../../../HotelsMap/redux/actions';
import {selectedPlacesSelector} from '../../../HotelsMap/redux/selectors';
// TYPES
import {GoogleMapLocationType} from '../../../HotelsMap/redux/types';
// COMPONENTS
import GoogleMap from './GoogleMap/GoogleMap';
import LocationDataAccordion from '../../../../../stories/components/hotels/DetailsPageUnif/LocationDataAccordion/LocationDataAccordion';
import {
  hotelDetailsSelector,
  poiDataSelector,
  hotelNameSelector,
  reviewRatingSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';
// UTILS
import {isValidArray} from '@stencil/hotels-utils/src/common-utils';
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  detailsClick,
  DetailsPageComponents,
} from '@stencil/omniture-analytics/src/constants/details';
import LocationInfoIcon from '../../../../../stories/Icons/LocationInfoIcon';
import {pokusGoogleMapStaticFn} from '@stencil/hotels-common-reducer/src/selectors';

const {DetailsGAClickEventActions, detailsPageClickEventCategoryName} = gaConfig;

enum LocationMapStateActionTypes {
  UpdateUserSearchedLocations = 'LocationMapStateActionTypes/UpdateUserSearchedLocations',
  RemoveUserSearchedLocations = 'LocationMapStateActionTypes/RemoveUserSearchedLocations',
  UpdateError = 'LocationMapStateActionTypes/UpdateError',
}

const reducer = (state, action) => {
  switch (action.type) {
    case LocationMapStateActionTypes.UpdateUserSearchedLocations:
      const localStateCopy = {...state};

      let normalisedIncomingPlaces = action.payload?.userSearchedLocations;

      if (!Array.isArray(action.payload?.userSearchedLocations)) {
        normalisedIncomingPlaces = [action.payload?.userSearchedLocations];
      }

      if (localStateCopy.userSearchedLocations.length > 3) {
        localStateCopy.userSearchedLocations = localStateCopy.userSearchedLocations.slice(1);
      }

      const incomingPlaceAlreadyPresent = normalisedIncomingPlaces.some(place => {
        return localStateCopy.userSearchedLocations.some(prevPlace => prevPlace._id === place._id);
      });

      return {
        ...state,
        userSearchedLocations: [
          ...(!incomingPlaceAlreadyPresent ? localStateCopy.userSearchedLocations : []),
          ...normalisedIncomingPlaces,
        ],
      };

    case LocationMapStateActionTypes.RemoveUserSearchedLocations:
      return {
        ...state,
        userSearchedLocations: state.userSearchedLocations.filter(
          place => place._id !== action.payload?.placeId,
        ),
      };

    case LocationMapStateActionTypes.UpdateError:
      return {
        ...state,
        error: action.payload?.error,
      };

    default:
      throw new Error(`${action.type} is not supported in LocationMap state reducer`);
  }
};

const initState = {
  userSearchedLocations: [],
  error: {},
};

const LocationMapNew = () => {
  const [state, stateDispatch] = useReducer(reducer, initState);
  const [toggle, setToggle] = useState(false);
  const [active, setActive] = useState<{type: GoogleMapLocationType; index: number}>({
    type: GoogleMapLocationType.Landmark,
    index: -1,
  });
  const [showMap, setShowMap] = useState(false);

  const locationAutosuggestRef = useRef(null);
  const mapContainerRef = useRef(null);

  const poiDataSelectorData = useSelector(poiDataSelector);
  const [activeAccordionTab, setActiveAccordionTab] = useState(
    poiDataSelectorData?.[0]?.['category_type'] || '',
  );
  const hotelDetailsSelectorData = useSelector(hotelDetailsSelector);
  const showGoogleStaticMap = useSelector(pokusGoogleMapStaticFn);

  const poiFirstData = poiDataSelectorData && poiDataSelectorData[0]?.category_data;

  useEffect(() => {
    if (isValidArray(poiDataSelectorData)) {
      setActiveAccordionTab(poiDataSelectorData[0]['category_type']);
    }
  }, [poiDataSelectorData]);

  const nearByDataList = [];
  if (isValidArray(poiFirstData)) {
    poiFirstData.forEach(item => {
      nearByDataList.push({
        crd: [Number(item?.location?.lat), Number(item?.location?.lon)],
        dis: Number(item?.distance),
        n: item?.place_name,
        unit_display: item?.distanceUnit,
        type: poiDataSelectorData?.[0]?.category_type || 'Landmarks',
      });
    });
  }
  const selectedPlaces = useSelector(selectedPlacesSelector);
  const propertyNameOriginal = useSelector(hotelNameSelector);
  const propertyName =
    !propertyNameOriginal || propertyNameOriginal?.length <= 143
      ? propertyNameOriginal
      : `${propertyNameOriginal?.substring(0, 140)}...`;

  const dispatch = useDispatch();

  const updateUserSearchedLocations = useCallback(
    locations => {
      stateDispatch({
        type: LocationMapStateActionTypes.UpdateUserSearchedLocations,
        payload: {
          userSearchedLocations: locations,
        },
      });
    },
    [stateDispatch],
  );

  const removeUserSearchedLocation = useCallback(
    place => {
      stateDispatch({
        type: LocationMapStateActionTypes.RemoveUserSearchedLocations,
        payload: {
          placeId: place,
        },
      });
    },
    [stateDispatch],
  );

  useEffect(() => {
    if (!propertyName) return;
    dispatch(resetSelectedPlaces());
    state.userSearchedLocations.forEach(location => {
      removeUserSearchedLocation(location._id);
    });
    locationAutosuggestRef.current.value = '';
    if (isValidArray(poiDataSelectorData)) {
      setActiveAccordionTab(poiDataSelectorData[0]['category_type']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, propertyName, setActiveAccordionTab]);

  const setSelectedPlaces = places => dispatch(updateSelectedPlaces(places));
  const showHotel = () => dispatch(showOnlyUserSelectedLocations());

  const getPlaceEventPropsForMapInteraction = ({place, typeOfPlace, error, index}) => {
    return {
      onClick() {
        const updatedPlace = {...place, type: typeOfPlace};

        const eventLabel = `Mapwidget_${place}_${typeOfPlace}_${index}`;

        const eventAction = toggle
          ? DetailsGAClickEventActions.locationMap.subLocationClickExpandedMap
          : DetailsGAClickEventActions.locationMap.subLocationClick;

        dispatch(
          omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
            cta: {
              name: `${DetailsPageComponents.GoogleMap}_${eventAction}_${eventLabel}`,
              type: omnitureConfig.ctaType.Button,
              componentName: DetailsPageComponents.GoogleMap,
              destination: '',
            },
          }),
        );

        if (error.message) {
          return;
        }
        if (showGoogleStaticMap) {
          handleLoadMapClick();
          setTimeout(() => {
            setActive({type: typeOfPlace, index});
            setSelectedPlaces(updatedPlace);
          }, 1000);
        } else {
          setActive({type: typeOfPlace, index});
          setSelectedPlaces(updatedPlace);
        }
      },
    };
  };

  const toggleAccordion = activeItem => {
    // dispatch(
    //   detailsPagePushGaThunk({
    //     eventCategory: detailsPageClickEventCategoryName,
    //     eventAction: DetailsGAClickEventActions.locationMap.othersExpanded,
    //   }),
    // );

    setActiveAccordionTab(prevState => {
      return prevState !== activeItem ? activeItem : '';
    });
  };

  const thereAreSelectedPlacesOnMapWhichAreNotUserSelected = !!selectedPlaces.filter(
    place => place.type !== GoogleMapLocationType.UserSelected,
  ).length;
  const anyNearbyLocationIsNotSelectedOnMap = !selectedPlaces.some(
    place =>
      place.type === GoogleMapLocationType.Landmark ||
      place.type === GoogleMapLocationType.Transit ||
      place.type === GoogleMapLocationType.Others,
  );
  const isSelectedPlacesIncludesAllNearbyLocations = useMemo(() => {
    return JSON.stringify(selectedPlaces) === JSON.stringify(nearByDataList);
  }, [selectedPlaces, nearByDataList]);
  const showAllNearbyPlacesShouldBeVisible = anyNearbyLocationIsNotSelectedOnMap;
  const showHotelShouldBeVisible = thereAreSelectedPlacesOnMapWhichAreNotUserSelected;
  const thereIsError = state.error?.message;
  const thereIsNoError = !thereIsError;
  const reviewRatingData = useSelector(reviewRatingSelector);

  const {giDataExists, ugcSrc, giData, extDataExists} = reviewRatingData || {};
  const showRating =
    !extDataExists && giDataExists && (ugcSrc === 'gi' || ugcSrc === 'ta') && giData?.hotelRating;

  const handleLoadMapClick = () => {
    if (showMap) return;
    setShowMap(true);
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.GoogleMap}_shown`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.GoogleMap,
          destination: '',
        },
      }),
    );
  };

  return (
    <S.LocationMapContainer id={detailsNavigationIds.location}>
      <S.LocationMapHeader>
        <LargeSectionHeader>Location {propertyName ? `of ${propertyName}` : ''}</LargeSectionHeader>
        <S.LocationSubTextPara
          itemScope
          itemProp="addressLocality"
          itemType="https://schema.org/addressLocality"
        >
          <LocationInfoIcon itemProp="streetAddress" margin="0.5rem 0 0 0" width="2.5rem" />
          {`${hotelDetailsSelectorData?.address.line1}`}
        </S.LocationSubTextPara>
        {showRating ? (
          <S.LocationSubTextPara>
            <S.SubTextSpan>
              The property has a location score of
              <S.LocationRating data-testid="d-LocationMap-rating">
                {giData?.hotelRating}/5
              </S.LocationRating>
            </S.SubTextSpan>
          </S.LocationSubTextPara>
        ) : null}
      </S.LocationMapHeader>

      {thereIsNoError && !toggle && (
        <S.SearchNearByLocation>
          <S.SearchIconWrap />
          <S.LocationSearchInput
            data-testid="d-LocationMap-input"
            placeholder="Search Area, Landmark or Transit nearby"
            ref={locationAutosuggestRef}
            onBlur={event =>
              dispatch(
                detailsPagePushGaThunk({
                  eventCategory: detailsPageClickEventCategoryName,
                  eventAction: DetailsGAClickEventActions.locationMap.searchMade,
                  eventLabel: event.target.value,
                }),
              )
            }
            onFocus={() => {
              dispatch(
                detailsPagePushGaThunk({
                  eventCategory: detailsPageClickEventCategoryName,
                  eventAction: DetailsGAClickEventActions.locationMap.mapAutosuggestOpened,
                }),
              );
            }}
            onChange={e => {
              if (showGoogleStaticMap && e?.target?.value?.length === 1) {
                handleLoadMapClick();
              }
            }}
          />
        </S.SearchNearByLocation>
      )}
      <S.LocationMapNewSection isExpand={toggle}>
        {!showHotelShouldBeVisible && showAllNearbyPlacesShouldBeVisible && thereIsNoError && (
          <S.MapActionButtonContainer isExpand={toggle}>
            <S.MapActionButton
              onClick={() => {
                dispatch(
                  detailsPagePushGaThunk({
                    eventAction: DetailsGAClickEventActions.locationMap.actionButton,
                    eventLabel: 'SHOW_ALL_PLACES',
                  }),
                );
                if (showGoogleStaticMap) {
                  setShowMap(true);
                  setTimeout(() => {
                    dispatch(showAllNearbyPlaces(nearByDataList));
                  }, 1000);
                } else {
                  dispatch(showAllNearbyPlaces(nearByDataList));
                }
              }}
            >
              Show All Nearby Places
            </S.MapActionButton>
          </S.MapActionButtonContainer>
        )}

        {showHotelShouldBeVisible && thereIsNoError && (
          <S.MapActionButtonContainer isExpand={toggle}>
            <S.MapActionButton
              onClick={() => {
                dispatch(
                  detailsPagePushGaThunk({
                    eventAction: DetailsGAClickEventActions.locationMap.actionButton,
                    eventLabel: 'SHOW_ONLY_HOTEL',
                  }),
                );
                showHotel();
              }}
            >
              Show Hotel
            </S.MapActionButton>
          </S.MapActionButtonContainer>
        )}
        {/* Left Component */}
        <S.MapLeft>
          {thereIsError && <SomethingWentWrongIcon />}
          {thereIsNoError &&
            (showGoogleStaticMap ? (
              showMap ? (
                <>
                  <GoogleMap
                    lat={hotelDetailsSelectorData?.lat}
                    lng={hotelDetailsSelectorData?.lng}
                    selectedPlaces={selectedPlaces}
                    refs={{
                      mapContainerRef,
                      locationAutosuggestRef,
                    }}
                    toggle={toggle}
                    setToggle={setToggle}
                    isSelectedPlacesIncludesAllNearbyLocations={
                      isSelectedPlacesIncludesAllNearbyLocations
                    }
                    updateUserSearchedLocations={updateUserSearchedLocations}
                    userSearchedLocations={state.userSearchedLocations}
                    onAPILoadError={() => {
                      stateDispatch({
                        type: LocationMapStateActionTypes.UpdateError,
                        payload: {
                          error: {
                            message: 'Google API Call Failed',
                          },
                        },
                      });
                    }}
                    onAPILoadSuccess={() => {
                      stateDispatch({
                        type: LocationMapStateActionTypes.UpdateError,
                        payload: {
                          error: {},
                        },
                      });
                    }}
                    hotelName={propertyNameOriginal}
                  />
                  {toggle && (
                    <S.SearchBar>
                      <S.LocationSearchInput
                        placeholder="See distance from"
                        id="fullScreenInput"
                        ref={locationAutosuggestRef}
                        onBlur={event =>
                          dispatch(
                            detailsPagePushGaThunk({
                              eventCategory: detailsPageClickEventCategoryName,
                              eventAction:
                                DetailsGAClickEventActions.locationMap.searchMadeExpanded,
                              eventLabel: event.target.value,
                            }),
                          )
                        }
                        onFocus={() => {
                          dispatch(
                            detailsPagePushGaThunk({
                              eventCategory: detailsPageClickEventCategoryName,
                              eventAction:
                                DetailsGAClickEventActions.locationMap.mapAutosuggestOpenedExpanded,
                            }),
                          );
                        }}
                      />
                      {state.userSearchedLocations.length > 0 ? (
                        <S.ClearSearchText
                          onClick={() => {
                            locationAutosuggestRef.current.value = '';
                            // showHotel() to be used if there are mulitple userSearchedLocations that are being saved
                            removeUserSearchedLocation(state.userSearchedLocations[0]._id);
                          }}
                        >
                          Clear Search
                        </S.ClearSearchText>
                      ) : (
                        <S.SearchIconWrap />
                      )}
                    </S.SearchBar>
                  )}
                </>
              ) : (
                <>
                  <S.GoogleStaticImageContainer
                    hotelImage={hotelDetailsSelectorData?.mapImageUrl}
                  ></S.GoogleStaticImageContainer>
                  <S.LoadMapButton onClick={handleLoadMapClick}>Load Map</S.LoadMapButton>
                </>
              )
            ) : (
              <>
                <GoogleMap
                  lat={hotelDetailsSelectorData?.lat}
                  lng={hotelDetailsSelectorData?.lng}
                  selectedPlaces={selectedPlaces}
                  refs={{
                    mapContainerRef,
                    locationAutosuggestRef,
                  }}
                  toggle={toggle}
                  setToggle={setToggle}
                  isSelectedPlacesIncludesAllNearbyLocations={
                    isSelectedPlacesIncludesAllNearbyLocations
                  }
                  updateUserSearchedLocations={updateUserSearchedLocations}
                  userSearchedLocations={state.userSearchedLocations}
                  onAPILoadError={() => {
                    stateDispatch({
                      type: LocationMapStateActionTypes.UpdateError,
                      payload: {
                        error: {
                          message: 'Google API Call Failed',
                        },
                      },
                    });
                  }}
                  onAPILoadSuccess={() => {
                    stateDispatch({
                      type: LocationMapStateActionTypes.UpdateError,
                      payload: {
                        error: {},
                      },
                    });
                  }}
                  hotelName={propertyNameOriginal}
                />
                {toggle && (
                  <S.SearchBar>
                    <S.LocationSearchInput
                      placeholder="See distance from"
                      id="fullScreenInput"
                      ref={locationAutosuggestRef}
                      onBlur={event =>
                        dispatch(
                          detailsPagePushGaThunk({
                            eventCategory: detailsPageClickEventCategoryName,
                            eventAction: DetailsGAClickEventActions.locationMap.searchMadeExpanded,
                            eventLabel: event.target.value,
                          }),
                        )
                      }
                      onFocus={() => {
                        dispatch(
                          detailsPagePushGaThunk({
                            eventCategory: detailsPageClickEventCategoryName,
                            eventAction:
                              DetailsGAClickEventActions.locationMap.mapAutosuggestOpenedExpanded,
                          }),
                        );
                      }}
                    />
                    {state.userSearchedLocations.length > 0 ? (
                      <S.ClearSearchText
                        onClick={() => {
                          locationAutosuggestRef.current.value = '';
                          // showHotel() to be used if there are mulitple userSearchedLocations that are being saved
                          removeUserSearchedLocation(state.userSearchedLocations[0]._id);
                        }}
                      >
                        Clear Search
                      </S.ClearSearchText>
                    ) : (
                      <S.SearchIconWrap />
                    )}
                  </S.SearchBar>
                )}
              </>
            ))}

          {thereIsError && (
            <div>
              <S.SomethingWentWrongText>
                Something went wrong! Reload to view Maps
              </S.SomethingWentWrongText>
              <S.ReloadBtn
                onClick={() => {
                  if (window) {
                    window.location.reload();
                  }
                }}
              >
                Reload Now
              </S.ReloadBtn>
            </div>
          )}
        </S.MapLeft>
        {/* Right Component */}
        {nearByDataList.length > 0 ? (
          <S.MapRight>
            <S.RightOuterContainer>
              <LocationDataAccordion
                active={active}
                getPlaceEventPropsForMapInteraction={getPlaceEventPropsForMapInteraction}
                state={state}
                poiDataSelectorData={poiDataSelectorData}
                activeAccordionTab={activeAccordionTab}
                toggleAccordion={toggleAccordion}
                selectedPlaces={selectedPlaces}
              />
            </S.RightOuterContainer>
          </S.MapRight>
        ) : null}
      </S.LocationMapNewSection>
      <S.AutoSuggestOuterWrapper data-testid="d-LocationMap-inputlist">
        {state.userSearchedLocations.map((location, index) => {
          return (
            <S.AutoSuggestContainer>
              <CenteredSpaceWrap>
                <S.LocationMapWrapper
                  {...getPlaceEventPropsForMapInteraction({
                    place: location,
                    typeOfPlace: GoogleMapLocationType.Others,
                    error: state.error,
                    index,
                  })}
                >
                  <MapLocationIcon />
                  {location.n && (
                    <S.LocationName>
                      {location.n}
                      <S.LocationNameToolTip>{location.n}</S.LocationNameToolTip>
                    </S.LocationName>
                  )}
                </S.LocationMapWrapper>
                <CloseCircleFillIcon
                  onClick={() => {
                    removeUserSearchedLocation(location._id);
                  }}
                />
              </CenteredSpaceWrap>
              <S.TimeDistanceDurationWrapper>
                {location.timeFromHotel && (
                  <S.TimeDuration>{location.timeFromHotel} |</S.TimeDuration>
                )}
                {location.distanceFromHotel && (
                  <S.DistanceDuration>{location.distanceFromHotel}</S.DistanceDuration>
                )}
                <CarIcon />
              </S.TimeDistanceDurationWrapper>
            </S.AutoSuggestContainer>
          );
        })}
      </S.AutoSuggestOuterWrapper>
    </S.LocationMapContainer>
  );
};
export default LocationMapNew;
